import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Activity, Lock, ScanLine, Sparkles, Loader2, Cpu, Wifi, FileLock2, Key, Server, Bell, BellOff } from "lucide-react";
import { api, aiQuick } from "../api";
import { useAuth } from "../AuthContext";
import { getPushState, subscribePush, unsubscribePush, testPush } from "../notifications";

export default function Security() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiQ, setAiQ] = useState("");
  const [aiOut, setAiOut] = useState("");
  const [encOn, setEncOn] = useState(false);
  const [encBusy, setEncBusy] = useState(false);
  const [push, setPush] = useState({ supported: false, permission: "default", subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/security/status");
    setStatus(data);
    if (data.last_scan) setScan(data.last_scan);
    setEncOn(!!user?.encryption_enabled);
    setPush(await getPushState());
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const toggleEncryption = async () => {
    setEncBusy(true);
    try {
      const { data } = await api.put("/security/encryption", { enabled: !encOn });
      setEncOn(data.enabled);
      await refresh();
    } catch {}
    setEncBusy(false);
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (push.subscribed) await unsubscribePush();
      else await subscribePush();
      setPush(await getPushState());
    } catch (e) {
      alert(e.message || "Push toggle failed.");
    }
    setPushBusy(false);
  };

  const sendTest = async () => {
    try { await testPush(); } catch {}
  };

  const runScan = async (deep = false) => {
    setScanning(true);
    setProgress(0);
    setScan(null);
    const tick = setInterval(() => setProgress(p => Math.min(95, p + (deep ? 4 : 9))), 150);
    try {
      const { data } = await api.post("/security/scan", { deep });
      clearInterval(tick);
      setProgress(100);
      setScan(data);
      await api.post("/security/log", data);
    } catch {
      clearInterval(tick);
    }
    setTimeout(() => { setScanning(false); setProgress(0); }, 400);
  };

  const askAI = async () => {
    if (!aiQ.trim()) return;
    setAiBusy(true);
    setAiOut("");
    try {
      const r = await aiQuick(
        `HEXA OS Security Advisor. User question: ${aiQ}\n\nContext: HEXA OS is a sandboxed web-based AI operating system. All apps run inside the browser security model with no native FS or kernel access.`,
        "You are an AI security advisor for HEXA OS. Be concise, practical, and reassuring without overpromising. Plain text, 4-6 sentences max."
      );
      setAiOut(r.text);
    } catch { setAiOut("AI unavailable."); }
    setAiBusy(false);
  };

  const threats = scan?.threats ?? 0;
  const protectedNow = threats === 0;

  return (
    <div className="h-full flex flex-col" data-testid="app-security">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {protectedNow ? (
            <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <ShieldCheck size={22} className="text-emerald-400" />
            </div>
          ) : (
            <div className="w-12 h-12 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
              <ShieldAlert size={22} className="text-red-400" />
            </div>
          )}
          <div>
            <div className="text-sm font-medium tracking-tight">{protectedNow ? "HEXA OS is protected" : `${threats} threats detected`}</div>
            <div className="text-[11px] text-white/50">{status?.engine || "HEXA Shield v1.0"} · Sandbox: {status?.sandbox || "Browser"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="sec-scan-quick" disabled={scanning} onClick={() => runScan(false)} className="btn-ghost text-xs flex items-center gap-1">
            {scanning ? <Loader2 size={12} className="animate-spin" /> : <ScanLine size={12} />} Quick Scan
          </button>
          <button data-testid="sec-scan-deep" disabled={scanning} onClick={() => runScan(true)} className="btn-accent text-xs flex items-center gap-1">
            <ScanLine size={12} /> Deep Scan
          </button>
        </div>
      </div>

      {scanning && (
        <div className="px-4 py-3 border-b border-white/10">
          <div className="text-[11px] text-white/60 mb-1.5 flex items-center gap-2">
            <Activity size={11} className="text-[#ff7a2e]" /> Scanning sandbox subsystems… {progress}%
          </div>
          <div className="h-1 bg-white/8 rounded-full overflow-hidden">
            <div className="h-full bg-[#ff7a2e] transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 grid grid-cols-3 gap-3 content-start">
        <KPI icon={Lock} label="Encryption" value="TLS · in-memory" data-testid="kpi-encryption" />
        <KPI icon={Cpu} label="Sandbox" value="Browser V8" />
        <KPI icon={Wifi} label="Outbound" value="API only" />
        <KPI icon={Key} label="AI keys" value="Verified" />
        <KPI icon={FileLock2} label="Virtual FS" value="Isolated" />
        <KPI icon={Server} label="Service Worker" value="Active" />

        <div className="col-span-3 p-4 rounded-lg border border-white/10 bg-white/3">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1.5">
            <Sparkles size={11} color="#ff7a2e" /> Latest Scan Results
          </div>
          {scan ? (
            <>
              <div className="text-xs text-white/80 mb-2">{scan.briefing}</div>
              <div className="grid grid-cols-2 gap-2">
                {scan.components?.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <ShieldCheck size={11} className="text-emerald-400" />
                    <span className="font-medium">{c.name}</span>
                    <span className="text-white/50">— {c.detail}</span>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-white/40 mt-3 mono">Scanned at {new Date(scan.scanned_at).toLocaleString()}</div>
            </>
          ) : (
            <div className="text-xs text-white/50">Run a scan to view results.</div>
          )}
        </div>

        <div className="col-span-3 p-4 rounded-lg border border-white/10 bg-white/3">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3 flex items-center gap-1.5">
            <Sparkles size={11} color="#ff7a2e" /> Privacy & Notifications
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-md border border-white/10 bg-white/5">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Lock size={13} color={encOn ? "#10b981" : "#71717a"} />
                  Encrypt mail at rest
                </div>
                <button data-testid="sec-toggle-encryption" onClick={toggleEncryption} disabled={encBusy} className={`w-10 h-5 rounded-full transition relative ${encOn ? "bg-emerald-500" : "bg-white/15"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition ${encOn ? "left-5" : "left-0.5"}`} />
                </button>
              </div>
              <div className="text-[11px] text-white/50">
                When enabled, new mail bodies are encrypted with AES-128/Fernet before being stored in MongoDB. Decrypted automatically on read for you.
              </div>
              {encOn && <div className="text-[10px] mt-1 text-emerald-400 flex items-center gap-1"><Lock size={9} /> Encryption active</div>}
            </div>

            <div className="p-3 rounded-md border border-white/10 bg-white/5">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {push.subscribed ? <Bell size={13} color="#ff7a2e" /> : <BellOff size={13} className="text-white/40" />}
                  Push notifications
                </div>
                {push.supported ? (
                  <button data-testid="sec-toggle-push" onClick={togglePush} disabled={pushBusy} className={`w-10 h-5 rounded-full transition relative ${push.subscribed ? "bg-[#ff7a2e]" : "bg-white/15"}`}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition ${push.subscribed ? "left-5" : "left-0.5"}`} />
                  </button>
                ) : (
                  <span className="text-[10px] text-white/40">unsupported</span>
                )}
              </div>
              <div className="text-[11px] text-white/50">
                Receive web push notifications via the service worker — new mail, meeting invites, security alerts. Works even when HEXA OS isn't open.
              </div>
              {push.subscribed && (
                <button data-testid="sec-push-test" onClick={sendTest} className="btn-ghost text-[10px] mt-1.5 px-2 py-1">Send test push</button>
              )}
              {push.permission === "denied" && (
                <div className="text-[10px] text-red-400 mt-1">Permission denied. Enable notifications for this site in your browser settings.</div>
              )}
            </div>
          </div>
        </div>

        <div className="col-span-3 p-4 rounded-lg border border-white/10 bg-white/3">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1.5">
            <Sparkles size={11} color="#ff7a2e" /> AI Security Advisor
          </div>
          <div className="flex gap-2 mb-2">
            <input
              data-testid="sec-ai-input"
              value={aiQ}
              onChange={(e) => setAiQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && askAI()}
              placeholder="Ask anything about HEXA security…"
              className="flex-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-xs"
            />
            <button data-testid="sec-ai-ask" disabled={aiBusy} onClick={askAI} className="btn-accent text-xs flex items-center gap-1">
              {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Ask
            </button>
          </div>
          {aiOut && <div className="text-xs whitespace-pre-wrap text-white/85 bg-white/5 border border-white/10 rounded p-3">{aiOut}</div>}
        </div>

        <div className="col-span-3 text-[11px] text-white/40">
          <strong className="text-white/60">Why HEXA OS is hard to compromise:</strong> Every app runs inside your browser's V8 sandbox. There is no kernel, no native filesystem, no executable installer surface, no driver layer, and no privilege escalation path. Outbound traffic is restricted to the HEXA API. Traditional malware/viruses cannot infect a sandboxed web OS.
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <div className="p-3 rounded-lg border border-white/10 bg-white/3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-md bg-white/5 flex items-center justify-center">
        <Icon size={14} color="#ff7a2e" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
        <div className="text-xs">{value}</div>
      </div>
    </div>
  );
}
